import { ISetting } from '@biolibtech/biolib-js';

export enum ChainType {
    EMPTY = 'empty',
    PROTEIN = 'Protein',
    DNA = 'DNA',
    RNA = 'RNA',
    CCD = 'CCD',
    SMILES = 'Smiles',
}

const CHAIN_TYPE_SETS: Record<ChainType, string> = {
    [ChainType.EMPTY]: '',
    [ChainType.PROTEIN]: 'acdefghiklmnpqrstvwybxzACDEFGHIKLMNPQRSTVWYBXZ',
    [ChainType.DNA]: 'ATCGatcg',
    [ChainType.RNA]: 'AUCGaucg',
    [ChainType.CCD]: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789',
    [ChainType.SMILES]: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789@+-=#$%()[]\\/.',
};

const CCD_CHARS = new Set(CHAIN_TYPE_SETS[ChainType.CCD].split(''));

const isCCDSequence = (sequence: string): boolean =>
    sequence.length === 3 && sequence.split('').every((char) => CCD_CHARS.has(char));

export function detectChainType(sequence: string): ChainType {
    if (!sequence.trim()) {
        return ChainType.EMPTY;
    }

    if (isCCDSequence(sequence)) {
        return ChainType.CCD;
    }

    const checkOrder = [ChainType.DNA, ChainType.RNA, ChainType.PROTEIN, ChainType.SMILES];
    const sequenceChars = new Set(sequence.split(''));

    for (const chainType of checkOrder) {
        const allowedChars = new Set(CHAIN_TYPE_SETS[chainType].split(''));
        const hasInvalidChars = Array.from(sequenceChars).some((char) => !allowedChars.has(char));

        if (!hasInvalidChars) {
            return chainType;
        }
    }

    return ChainType.PROTEIN;
}

export function findInvalidSequenceCharacters(sequence: string, chainType: ChainType): string[] {
    if (chainType === ChainType.EMPTY) {
        return [];
    }

    const allowedChars = new Set(CHAIN_TYPE_SETS[chainType].split(''));
    return Array.from(sequence).filter((char) => !allowedChars.has(char));
}

export function validateMultiChainCompoundRows(
    rows: Record<number, Record<number, string>>,
    setting: ISetting,
): null | string {
    const nameToRowNumber: Record<string, number> = {};
    let isEmpty = true;
    for (const [rowIndex, columns] of Object.entries(rows)) {
        if (columns[0] === '' && columns[1] === '' && columns[2] === '') {
            continue;
        }

        const rowNumber = Number(rowIndex) + 1;
        const generateDefaultName = (index: number): string => {
            if (index < 26) {
                return String.fromCharCode(65 + index);
            } else {
                const firstChar = String.fromCharCode(65 + Math.floor(index / 26) - 1);
                const secondChar = String.fromCharCode(65 + (index % 26));
                return firstChar + secondChar;
            }
        };
        const name = columns[0].trim().replace('>', '') || generateDefaultName(Number(rowIndex));
        const sequence = columns[1].trim();
        const chainType = columns[2].trim();

        const maxChainNameLength = setting.config?.max_chain_name_length
            ? parseInt(setting.config.max_chain_name_length as string, 10)
            : 4;
        if (name.length > maxChainNameLength) {
            return `Chain name "${name}" in row ${rowIndex + 1} is too long (max ${maxChainNameLength} characters).`;
        }

        const allowedNameChars = new Set(
            'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789|-_.:*#,=+()'.split(''),
        );
        const invalidNameChars = Array.from(name).filter((char) => !allowedNameChars.has(char));
        if (invalidNameChars.length > 0) {
            return `Chain "${name}" contains invalid character ("${invalidNameChars[0]}").`;
        }

        if (sequence) {
            if (/\s/.test(name)) {
                return `Chain "${name}" cannot have spaces in the name.`;
            }
            if (/\s/.test(sequence)) {
                return `Chain "${name}" cannot have spaces in the sequence.`;
            }

            if (name in nameToRowNumber) {
                return `Chain "${name}" must be unique across rows.`;
            }

            if (!chainType || chainType === ChainType.EMPTY) {
                return `Chain "${name}" requires a chain type.`;
            }

            const validChainTypes = Object.values(ChainType);
            if (!validChainTypes.includes(chainType as ChainType)) {
                return `Chain "${name}" has invalid chain type "${chainType}".`;
            }

            const invalidChars = findInvalidSequenceCharacters(sequence, chainType as ChainType);
            if (invalidChars.length > 0) {
                return `Chain "${name}" contains invalid sequence character ("${invalidChars[0]}") for chain type ${chainType}.`;
            }

            nameToRowNumber[name] = rowNumber;
        } else if (chainType && chainType !== ChainType.EMPTY) {
            return `Chain "${name}" requires a sequence.`;
        }

        if (name && sequence) {
            isEmpty = false;
        }
    }
    if (isEmpty && setting.required && !setting.default_value) {
        return 'Please fill out at least one row.';
    }
    return null;
}

export function validateAllRows(rows: Record<number, Record<number, string>>, setting: ISetting): string | null {
    return validateMultiChainCompoundRows(rows, setting);
}
